import styled from '@emotion/styled';
import React, { ChangeEvent, LegacyRef, RefObject } from 'react';
import { Input as SemanticInput, InputOnChangeData, StrictInputProps } from 'semantic-ui-react';
import tw, { theme } from 'twin.macro';
import { css, SerializedStyles } from '@emotion/react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Popup } from '../../Popup';
import Button from '../../Button/Button';

export interface InputProps extends Omit<StrictInputProps, 'onChange'> {
  id?: string;
  name?: string;
  inputRef?: RefObject<any>;
  ref?: LegacyRef<any>;
  value?: number | string | null;
  clearable?: boolean;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean; // Does this work?
  min?: number;
  max?: number;
  step?: number | 'any';
  novalidate?: boolean;
  css?: SerializedStyles;
  onClick?: (e: Event) => void;
  onChange?: (value: string) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
}

export const Input = styled<React.FC<InputProps>>(
  ({ inputRef, value, clearable, onChange, css: cssProps, ...props }) => {
    const action = clearable
      ? {
          color: 'white',
          icon: `close ${value ? 'black' : 'hidden'}`,
          size: 'medium',
          onClick: async (): Promise<void> => {
            onChange && onChange('');
          },
        }
      : undefined;

    return (
      <SemanticInput
        className={`
          appearance-none
          focus:outline-none
          focus:shadow
          border
          ${props.className}
        `}
        css={[
          css`
            ${cssProps}
            ${tw`
            block
            text-base
            font-light
            text-color-input-field
            leading-tight
            `}
            &.ui.input > input {
              border-radius: 6px;
            }
            > .ui.label {
              position: relative;
              left: -1px;
            }
            &::placeholder {
              opacity: 1;
              ${tw`text-color-input-placeholder`}
            }
            > button.ui.button {
              background: #fff;
              border: 1px solid ${theme`colors.color.input-box`};
              border-left: 0px solid #fff;
              position: relative;
              left: -1px;
            }
          `,
        ]}
        fluid
        action={action}
        ref={inputRef}
        value={value ?? ''}
        onChange={(_: ChangeEvent<HTMLInputElement>, data: InputOnChangeData): void => {
          onChange && void onChange(data.value);
        }}
        {...props}
      />
    );
  }
)``;

interface CopyToClipboardOptions {
  options?: {
    debug?: boolean;
    format?: 'text/html' | 'text/plain';
    message?: string;
  };
  text: string;
  className?: string;
  render?: (value: { copied: boolean }) => React.ReactElement;
  simple?: boolean;
  label?: string;
  button?: JSX.Element | string;
  disabled?: boolean;
  classNameWrapper?: string;
  leftButton?: boolean;
}

export const CopyToClipboardControl: React.FC<CopyToClipboardOptions> = ({
  options,
  text,
  label,
  className,
  render,
  simple,
  button,
  disabled,
  classNameWrapper,
  leftButton,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [isUrl, setIsUrl] = React.useState(false);
  const unmounted = React.useRef<boolean>(false);

  React.useEffect(() => {
    return (): void => {
      unmounted.current = true;
    };
  }, []);

  React.useEffect(() => {
    try {
      new URL(text);
      setIsUrl(true);
    } catch {
      setIsUrl(false);
    }
  }, [text]);

  React.useEffect(() => {
    if (copied) {
      setTimeout(() => {
        !unmounted.current && setCopied(false);
      }, 3000);
    }
  }, [copied]);

  if (render || simple) {
    return (
      <div
        className={`flex row cursor-pointer ${className ?? ''}`}
        onClick={(e): void => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <CopyToClipboard options={options} text={text} onCopy={(): void => setCopied(true)}>
          {render ? (
            render({ copied })
          ) : copied ? (
            <Popup className='ml-0' open={true} trigger={<span>{text}</span>}>
              Copied
            </Popup>
          ) : (
            <span>{text}</span>
          )}
        </CopyToClipboard>
      </div>
    );
  }

  return (
    <div
      className={`flex ${leftButton ? '' : 'flex-row-reverse'} ${classNameWrapper}`}
      css={css`
        ${tw`w-full`}
      `}
    >
      <CopyToClipboard
        options={options}
        text={text}
        onCopy={(): void => setCopied(true)}
        css={css`
          .input {
            max-width: auto;
          }
        `}
      >
        <Button
          disabled={disabled}
          css={[
            tw`flex flex-col justify-center items-center text-gray-1
            hover:text-color-input-field bg-white
            hover:bg-gray-100 rounded-input border
            border-color-input-box px-1e text-sm min-w-5e`,
            leftButton
              ? css`
                  border-top-right-radius: 0 !important;
                  border-bottom-right-radius: 0 !important;
                  border-right-width: 0;
                `
              : css`
                  border-top-left-radius: 0 !important;
                  border-bottom-left-radius: 0 !important;
                  border-left-width: 0;
                `,
          ]}
        >
          {button ? button : copied ? 'Copied' : 'Copy'}
        </Button>
      </CopyToClipboard>

      <div
        className={className}
        css={[
          tw`flex flex-1 bg-blue-1 border border-color-input-box rounded-input overflow-hidden items-center`,
          css`
            padding: 0.678571em 1em;
          `,
          leftButton
            ? css`
                border-top-left-radius: 0 !important;
                border-bottom-left-radius: 0 !important;
                border-left: 0;
              `
            : css`
                border-top-right-radius: 0 !important;
                border-bottom-right-radius: 0 !important;
                border-right: 0;
              `,
        ]}
        onClick={(e): void => {
          if (!isUrl) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <span
          className='flex-auto overflow-hidden items-center'
          css={css`
            text-overflow: ellipsis;
          `}
        >
          {label && <span className='mr-1e'>{label}</span>}
          {isUrl ? (
            <a
              className='text-link cursor-pointer underline truncate text-color-input-field hover:text-gray-1'
              href={text}
              target='_blank'
            >
              {text}
            </a>
          ) : (
            <Input className='p-0 border-none transparent' value={text} />
          )}
        </span>
      </div>
    </div>
  );
};

export default Input;
